import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { memberHandler, posHandler } from "../../redux/handlers";
import { useParams } from "react-router-dom";
import { nanoid } from "nanoid";

export default function PayPoint({
  payment,
  order,
  discount,
  settings,
  orderData,
  actions,
  ...props
}) {
  const dispatch = useDispatch();
  const { businessSlug } = useParams();

  const {
    selectedMethod,
    setSelectedMethod,
    setData: setPaymentData,
  } = payment;
  const { details: orderDetails } = order;
  const { refreshList } = settings;
  const { addPointPayment } = actions || {};

  const currentMember = useSelector((state) => state.member?.individualData);
  const [customerSelect, setCustomerSelect] = useState(null);
  const [usePoint, setUsePoint] = useState("0");
  const [discountValue, setDiscountValue] = useState(null);
  const [pointRateExchange, setPointRateExchange] = useState({
    name: "เงื่อนไข คะแนนที่ใช้ 0.- ได้รับส่วนลด 0.-",
    rate: 0,
  });
  const [errorMessage, setErrorMessage] = useState("");

  const totalAmount =
    orderDetails?.paymentNet !== undefined && orderDetails?.paymentNet !== null
      ? parseFloat(orderDetails.paymentNet)
      : 0;

  const formatToDisplay = useCallback((num) => {
    const numStr = num.toString();
    const [intPart, decimalPart = ""] = numStr.split(".");
    let formattedDecimal;
    if (decimalPart.length === 0) {
      formattedDecimal = "00";
    } else if (decimalPart.length === 1) {
      formattedDecimal = decimalPart + "0";
    } else {
      formattedDecimal = decimalPart.substring(0, 2);
    }
    return `${intPart}.${formattedDecimal}`;
  }, []);

  useEffect(() => {
    const fetchPointSettings = async () => {
      try {
        const response = await dispatch(
          posHandler.getBusinessPointSetting(businessSlug)
        );

        if (response) {
          const spendSetting = response.find(
            (setting) => setting.type === "SPEND" && setting.is_active
          );

          if (spendSetting) {
            const pointValue = parseFloat(spendSetting.point);
            const exchangeRate = parseFloat(spendSetting.exchange_rate);

            const discountPerPoint = exchangeRate / pointValue;

            setPointRateExchange({
              name: `เงื่อนไข คะแนนที่ใช้ ${pointValue} แต้ม ได้รับส่วนลด ${exchangeRate} บาท`,
              rate: discountPerPoint,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching point settings:", error);
      }
    };

    fetchPointSettings();
  }, [dispatch, businessSlug]);

  useEffect(() => {
    if (orderData && orderData.memberID) {
      dispatch(memberHandler.getMemberById(businessSlug, orderData.memberID));
    }
  }, [dispatch, businessSlug, orderData]);

  useEffect(() => {
    if (currentMember && orderData.memberID) {
      setCustomerSelect({
        id: currentMember.id,
        fullName: `${currentMember.first_name_th || ""} ${
          currentMember.last_name_th || ""
        }`.trim(),
        code: currentMember.code,
        tel: currentMember.phone_number,
        point: currentMember.total_point || 0,
        pointExpire: "31/12/2566",
      });
    }
  }, [currentMember, orderData.memberID]);

  useEffect(() => {
    if (!usePoint || usePoint === "0") {
      setDiscountValue(null);
      setErrorMessage("");
      return;
    }

    const pointValue = parseFloat(usePoint) || 0;

    if (
      customerSelect &&
      customerSelect.point !== null &&
      customerSelect.point !== undefined
    ) {
      if (pointValue > customerSelect.point) {
        setErrorMessage("คะแนนที่ใช้ต้องไม่เกินคะแนนที่มี");
        setDiscountValue(null);
        return;
      }
    }

    if (pointRateExchange.rate > 0) {
      let calculatedDiscount = pointValue * pointRateExchange.rate;
      calculatedDiscount = Math.floor(calculatedDiscount * 100) / 100;

      if (totalAmount && calculatedDiscount > totalAmount) {
        const maxPoints = Math.floor(totalAmount / pointRateExchange.rate);
        setUsePoint(String(maxPoints));
        setDiscountValue(formatToDisplay(totalAmount));
        setErrorMessage(`คะแนนถูกปรับให้พอดีกับยอดชำระ`);

        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
      } else {
        setErrorMessage("");
        setDiscountValue(formatToDisplay(calculatedDiscount));
      }
    } else {
      setDiscountValue("0.00");
    }
  }, [
    usePoint,
    pointRateExchange.rate,
    customerSelect,
    totalAmount,
    formatToDisplay,
  ]);

  const handlePointAdjustment = (amount) => {
    if (!customerSelect) {
      return;
    }

    let newValue = parseInt(usePoint || "0") + amount;

    if (newValue < 0) {
      newValue = 0;
    }

    if (customerSelect.point !== null && customerSelect.point !== undefined) {
      if (newValue > customerSelect.point) {
        newValue = customerSelect.point;
        setErrorMessage("คะแนนที่ใช้ต้องไม่เกินคะแนนที่มี");
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
      }
    }

    if (pointRateExchange.rate > 0 && totalAmount > 0) {
      let calculatedDiscount = newValue * pointRateExchange.rate;
      calculatedDiscount = Math.floor(calculatedDiscount * 100) / 100;

      if (calculatedDiscount > totalAmount && amount > 0) {
        setErrorMessage(
          "ไม่สามารถเพิ่มแต้มได้อีก เนื่องจากส่วนลดเกินยอดชำระแล้ว"
        );
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
        return;
      } else if (calculatedDiscount > totalAmount) {
        newValue = Math.floor(totalAmount / pointRateExchange.rate);
        setErrorMessage("ส่วนลดถูกปรับให้พอดีกับยอดชำระ");
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
      }
    }

    setUsePoint(String(newValue));
  };

  const isAddPointDisabled = useCallback(() => {
    if (!customerSelect || !pointRateExchange.rate || totalAmount <= 0)
      return true;

    const currentPoints = parseInt(usePoint || "0");
    const maxAllowedPoints = Math.floor(totalAmount / pointRateExchange.rate);

    return (
      currentPoints >= maxAllowedPoints || currentPoints >= customerSelect.point
    );
  }, [usePoint, customerSelect, pointRateExchange.rate, totalAmount]);

  const handleAddDiscount = useCallback(() => {
    if (
      !usePoint ||
      usePoint === "0" ||
      !customerSelect ||
      !discountValue ||
      discountValue === "Infinity"
    )
      return;

    const pointMethodId = selectedMethod?.id;

    const newPaymentItem = {
      id: nanoid(),
      channel: {
        ...selectedMethod,
        paymentChannelName: "ใช้คะแนน",
      },
      amount: discountValue,
      provider: null,
      remark: `คะแนนแลกส่วนลด ${usePoint} แต้ม`,
      status: 1,
      pointInfo: {
        point: usePoint,
        member_id: customerSelect.id,
        method_id: pointMethodId,
      },
    };

    if (addPointPayment) {
      addPointPayment(newPaymentItem);
    } else if (setPaymentData) {
      setPaymentData((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        newPaymentItem,
      ]);
    }

    if (setSelectedMethod) {
      setSelectedMethod({
        paymentChannelID: 1,
        paymentChannelName: "เงินสด",
        icon: "cash",
        code: "CASH",
      });

      if (typeof refreshList === "function") {
        refreshList();
      }
    }
  }, [
    usePoint,
    customerSelect,
    discountValue,
    selectedMethod,
    setSelectedMethod,
    refreshList,
    addPointPayment,
    setPaymentData,
  ]);

  const getRemainingPoints = useCallback(() => {
    if (!customerSelect || !usePoint) return customerSelect?.point || 0;

    const pointsUsed = parseFloat(usePoint) || 0;
    return Math.max(0, (customerSelect.point || 0) - pointsUsed);
  }, [customerSelect, usePoint]);

  const getOrderNo = useCallback(() => {
    return orderDetails?.orderNo || "-";
  }, [orderDetails]);

  const isConfirmDisabled =
    !usePoint ||
    usePoint === "0" ||
    !customerSelect ||
    !discountValue ||
    discountValue === "Infinity" ||
    parseFloat(usePoint) <= 0 ||
    parseFloat(discountValue) <= 0 ||
    pointRateExchange.rate <= 0 ||
    errorMessage !== "";

  return (
    <div className="h-100 d-flex flex-column">
      <div className="d-flex align-items-center text-[#061126] mb-3">
        <div className="me-auto">
          <span className="fs-18 fw-500">Order NO.</span>
          <span className="fs-14 fw-500 ms-2">{getOrderNo()}</span>
        </div>
        <div className="fs-18 fw-bold text-[#2DC76D]">คะแนนแลกส่วนลด</div>
      </div>

      <div className="fs-16 fw-500 mb-2 text-[#2DC76D]">ข้อมูลลูกค้า</div>

      <div
        className="p-4 text-[#2F4858] mb-4"
        style={{
          borderRadius: "15px",
          background: "rgba(223, 247, 233, 0.15)",
        }}
      >
        <div className="d-flex align-items-center mb-4">
          <div className="me-auto">
            <i className="icon-card me-2"></i>
            <span className="py-3">ชื่อลูกค้า</span>
          </div>
          <div>{customerSelect?.fullName || "-"}</div>
        </div>

        <div className="d-flex align-items-center mb-4">
          <div className="me-auto">
            <i className="icon-tel me-2"></i>
            <span className="py-3">เบอร์โทรศัพท์</span>
          </div>
          <div>{customerSelect?.tel || "-"}</div>
        </div>

        <div className="d-flex align-items-center">
          <div className="me-auto">
            <i className="icon-gift me-2"></i>
            <span className="py-3">คะแนนคงเหลือ</span>
          </div>
          <div className="text-[#FF731D]">
            <i className="icon-star"></i> {customerSelect?.point || "-"}
          </div>
        </div>
      </div>

      <div
        className="p-2 text-[#2F4858] mb-2"
        style={{
          background: "#FFF",
          filter: "drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.08))",
        }}
      >
        <div className="row gx-0 align-items-center">
          <div className="col-5">
            <div className="text-center text-[#061126] fs-12 fw-500 mb-2">
              คะแนนของคุณ
            </div>

            <div className="text-center d-flex align-items-center justify-content-center">
              <div
                className="icon-star"
                style={{ width: "24px", height: "24px" }}
              ></div>
              <span className="text-[#FF731D] fs-26 fw-bold">
                {customerSelect?.point || "-"}
              </span>
            </div>

            <div className="text-center fs-8 text-[#6D788D] mt-1 mb-3">
              หมดอายุ {customerSelect?.pointExpire || "-"}
            </div>

            <div className="row">
              <div className="col-6">
                <div className="text-center fs-10 mb-1">คะแนนคงเหลือ</div>
                <div className="text-center fs-18 fw-500 text-[#2DC76D]">
                  {customerSelect ? getRemainingPoints() : "-"}
                </div>
              </div>
              <div className="col-5">
                <div className="text-center fs-10 mb-1">ส่วนลด</div>
                <div className="text-center fs-18 fw-500 text-[#F95057]">
                  {discountValue && discountValue !== "Infinity"
                    ? "-" + formatToDisplay(discountValue || 0)
                    : "0"}
                </div>
              </div>
            </div>
          </div>

          <div className="col-7">
            <div
              className="py-3 px-2 text-[#2F4858] text-center"
              style={{ borderRadius: "8px", border: "1px solid #F4F4F4" }}
            >
              <div
                className="py-2 mb-3 text-center mx-auto text-[#474747]"
                style={{
                  fontSize: "10px",
                  background: "rgba(137, 130, 130, 0.08)",
                  borderRadius: "8px",
                }}
              >
                {pointRateExchange.name}
              </div>

              <div className="text-center fw-500 fs-10 mb-3">
                จำนวนคะแนนที่ต้องการใช้
              </div>

              {/* Point display and control section */}
              <div className="mb-3">
                <div
                  className="text-center fs-32 fw-bold"
                  style={{
                    borderRadius: "8px",
                    background: "#f8f9fa",
                    padding: "8px 15px",
                    marginBottom: "10px",
                  }}
                >
                  {usePoint}
                </div>

                {/* Point presets */}
                <div className="d-flex justify-content-center align-items-center mb-3">
                  <div className="text-[#474747] fs-12 me-2">
                    ระบุจำนวนคะแนนเช่น 100,500,1000
                  </div>
                </div>

                {/* Plus/Minus buttons */}
                <div className="d-flex justify-content-center gap-2">
                  <button
                    onClick={() => handlePointAdjustment(-100)}
                    className="btn btn-danger"
                    style={{
                      width: "60px",
                      borderRadius: "4px",
                      fontSize: "10px",
                    }}
                    disabled={
                      !customerSelect?.id ||
                      usePoint === "0" ||
                      parseInt(usePoint) < 100
                    }
                  >
                    - 100
                  </button>
                  <button
                    onClick={() => handlePointAdjustment(-10)}
                    className="btn btn-danger"
                    style={{
                      width: "60px",
                      borderRadius: "4px",
                      fontSize: "10px",
                    }}
                    disabled={!customerSelect?.id || usePoint === "0"}
                  >
                    - 10
                  </button>
                  <button
                    onClick={() => handlePointAdjustment(10)}
                    className="btn"
                    style={{
                      width: "60px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      backgroundColor: "#3c82f6",
                      color: "white",
                    }}
                    disabled={!customerSelect?.id || isAddPointDisabled()}
                  >
                    + 10
                  </button>
                  <button
                    onClick={() => handlePointAdjustment(100)}
                    className="btn"
                    style={{
                      width: "60px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      backgroundColor: "#3c82f6",
                      color: "white",
                    }}
                    disabled={!customerSelect?.id || isAddPointDisabled()}
                  >
                    + 100
                  </button>
                </div>
              </div>

              {errorMessage && (
                <div className="text-center text-danger fs-10 mt-1">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex pt-2 mt-auto">
        <button
          className="ms-auto btn btn-st-q1 px-5"
          onClick={handleAddDiscount}
          disabled={isConfirmDisabled}
        >
          ยืนยัน
        </button>
      </div>
    </div>
  );
}
